import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { isUserAuthenticated, logout as logoutUser } from '../components/verification/verificationApi';

interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    // Check authentication status on app load
    const authStatus = isUserAuthenticated();
    setIsAuthenticated(authStatus);
  }, []);

  const logout = React.useCallback(() => {
    logoutUser();
    setIsAuthenticated(false);
  }, []);

  const value = React.useMemo(() => ({
    isAuthenticated,
    setIsAuthenticated,
    logout
  }), [isAuthenticated, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
